import * as React from 'react'
import {
  RiskChallenge,
  RiskActions,
  RiskContext,
  RiskWorkflows,
} from '@doordash/component-risk'
import t from './strings'
import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import {
  ArchiveAccountState,
  Channel,
  PrivacyMode as Mode,
  RTIPrivacyFlow,
  RTFPrivacyFlow,
} from '../../../models/types'
import {
  Text,
  StackChildren,
  Inset,
} from '@doordash/design-language'
import ArchiveAccountSection from '../../molecules/archive-account-section'
import RequestArchive from '../../molecules/request-archive'
import PreparingArchive from '../../molecules/preparing-archive'
import * as api from '../../../api'
import * as events from '../../../events'
import * as DA from '../../../typeModules/events'

type Props = {
  trackGDPR?: (data: DA.RTI) => void
}

export const ManageAccount: React.FC<Props> = ({ trackGDPR }) => {
  const [archiveAccountState, setArchiveAccountState] = useState<
    ArchiveAccountState
  >()
  const { dispatch } = React.useContext(RiskContext)
  const [archiveRequestId, setArchiveRequestId] = useState<string>('')
  const [archiveRequestDate, setArchiveRequestDate] = useState<string>('')
  const [archiveExpiryDate, setArchiveExpiryDate] = useState<string>('')
  const [archiveDownloadLink, setArchiveDownloadLink] = useState<string>('')
  const [postMFAMode, setPostMFAMode] = useState<RTIPrivacyFlow | RTFPrivacyFlow>()

  const selectMode = (selectedMode: Mode) => {
    trackGDPR({ action: DA.GDPRActions.MfaRender })

    dispatch({
      type: RiskActions.RegisterCallback,
      onClose: () => {
        trackGDPR({ action: DA.GDPRActions.MfaClose })
        setPostMFAMode(null)
      },
      onSuccess: () => {
        trackGDPR({ action: DA.GDPRActions.MfaSuccess })
        if (selectedMode === Mode.RTI) {
          setPostMFAMode(RTIPrivacyFlow.Request)
        } else {
          setPostMFAMode(RTFPrivacyFlow.Confirm)
        }
      }
    })

    dispatch({
      type: RiskActions.VerificationRequired,
      workflow: RiskWorkflows.DasherLogin,
    })
  }

  useEffect(() => {
    const fetchAndParseData = async () => {
      try {
        const rtiRequestResponse = await api.getDasherRTIRequestList()
        const requests = rtiRequestResponse.data.requests

        if (requests.length === 0) {
          setArchiveAccountState(ArchiveAccountState.NoStatus)
          return
        }

        const pendingRequests = requests.filter(
          req => req.status === 'IN_PROGRESS'
        )
        if (pendingRequests.length > 0) {
          const pendingRequest = pendingRequests[0]
          setArchiveAccountState(ArchiveAccountState.Pending)
          setArchiveRequestDate(pendingRequest.created_at)
          return
        }

        const completedRequests = requests.filter(
          req => req.status === 'COMPLETE'
        )
        if (completedRequests.length > 0) {
          const completedRequest = completedRequests[0]
          const { data: archiveLink } = await api.getDasherRTIRequestArchive(completedRequest.request_id)
          setArchiveAccountState(ArchiveAccountState.Ready)
          setArchiveRequestDate(completedRequest.created_at)
          setArchiveExpiryDate(completedRequest.expires_at)
          setArchiveDownloadLink(archiveLink.url)
          setArchiveRequestId(completedRequest.request_id)
        }
      } catch (error) {
        console.log(error)
        setArchiveAccountState(ArchiveAccountState.NoStatus)
      }
    }

    fetchAndParseData()
  }, [])

  return (
    <React.Fragment>
      <RiskChallenge channel={Channel.Sms} />
      <Inset>
        {postMFAMode === RTIPrivacyFlow.Request && (
          <RequestArchive setView={setPostMFAMode} />
        )}
        {postMFAMode === RTIPrivacyFlow.PreparingArchive && (
          <PreparingArchive />
        )}
        {!postMFAMode && (
          <StackChildren size={StackChildren.Sizes.XLarge}>
            <Text styles={Text.Styles.PageTitle2}>{t.header()}</Text>
            <StackChildren size={StackChildren.Sizes.XxLarge}>
              <ArchiveAccountSection
                archiveRequestId={archiveRequestId}
                archiveAccountState={archiveAccountState}
                archiveRequestDate={archiveRequestDate}
                archiveExpiryDate={archiveExpiryDate}
                archiveDownloadLink={archiveDownloadLink}
                onModeChange={selectMode}
              />
            </StackChildren>
          </StackChildren>
        )}
      </Inset>
    </React.Fragment>
  )
}

const mapDispatchToProps = {
  trackGDPR: events.trackGDPR,
}

export default connect(null, mapDispatchToProps)(ManageAccount)
